"use client";

import React, { useRef, useEffect, useState } from "react";
import mapboxgl, { LngLatLike, Map, Marker } from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { debounce } from 'lodash';
import { SearchBox } from "@mapbox/search-js-react";
import type { SearchBoxRetrieveResponse } from "@mapbox/search-js-core";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { mapBoxConfig } from "src/mapbox-helpers/config";
import { useSnackbar } from "../components/SnackbarService";
import MapboxFeature from "src/mapbox-helpers/MapboxFeature";
import { ParsedFeatureData } from "src/mapbox-helpers/map-types";
import { roundToTwoDecimalPlaces } from "src/util/util";

// Set the Mapbox access token from the configuration.
mapboxgl.accessToken = mapBoxConfig.accessToken;

/**
 * Props for the MapBoxDrawer component.
 * @property onCompleteDraw - Callback function invoked when a drawing is completed or modified.
 *                            It receives the parsed feature data or null if no features are present.
 */
interface MapBoxDrawerProps {
    type: "POLYGON" | "POINT";
    onCompleteDraw: (data: ParsedFeatureData | null) => void;
}

/**
 * MapBoxDrawer is a React component that provides an interactive map
 * with drawing capabilities (polygons) and a search functionality.
 * It's designed to be used within a dialog or a similar container.
 */
const MapBoxDrawer: React.FC<MapBoxDrawerProps> = ({
    type,
    onCompleteDraw,
}) => {
    // Default map center (India) and zoom level.
    const initialCenter: [number, number] = [78.9629, 20.5937];
    const initialZoom = 5; // Initial zoom level to show a broad area.
    // State for displaying the area tooltip
    const [showAreaTooltip, setShowAreaTooltip] = useState(false);
    const [areaTooltipContent, setAreaTooltipContent] = useState('');

    // State for managing map's center and zoom level.
    const [mapCenter, setMapCenter] = useState<[number, number]>(initialCenter);
    const [mapZoom, setMapZoom] = useState<number>(initialZoom);
    const { showSnackbar, snackbarType } = useSnackbar();

    // Refs for map container, Mapbox map instance, and MapboxDraw instance.
    const mapContainer = useRef<HTMLDivElement | null>(null);
    const map = useRef<Map | null>(null);
    const draw = useRef<MapboxDraw | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false); // Tracks if the map's initial style and sources have loaded.

    // State for the search box input value and the coordinates of the marker for the searched location.
    const [searchMarkerCoordinates, setSearchMarkerCoordinates] = useState<[number, number] | null>(null);
    const [inputValue, setInputValue] = useState('');
    const searchMarkerRef = useRef<Marker | null>(null);

    useEffect(() => {

        if (map.current || !mapContainer.current) return;

        // Initialize the Mapbox map instance.
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/streets-v12",
            // Use state for center and zoom to allow dynamic updates.
            center: mapCenter,
            zoom: mapZoom,
            attributionControl: true,
        });

        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

        // Initialize MapboxDraw for drawing polygons.
        if (type === "POLYGON") {
            draw.current = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                    polygon: true,
                    trash: true,
                },
                defaultMode: "draw_polygon",
            });
            map.current.addControl(draw.current, "top-left");
        }

        if (type === "POINT") {
            // TODO add point draw tool
            draw.current = new MapboxDraw({
                controls: {
                    point: true,
                    trash: true,
                    polygon: false,
                    line_string: false,
                },
                defaultMode: "draw_point",
                displayControlsDefault: false,
            });
            map.current.addControl(draw.current, 'top-left');
        }

        map.current.on("load", () => {
            setIsMapLoaded(true);
        });

        // Function to handle updates to drawn features.
        const updateFeatures = () => {
            if (type === "POLYGON") {
                if (draw.current) {
                    const data = draw.current.getAll();
                    if (data.features.length > 0) {
                        const parseData: ParsedFeatureData | null = MapboxFeature.getParseFeatureData(data);
                        if (parseData) {
                            updateAreaTooltip(parseData);
                            onCompleteDraw(parseData);
                        } else {
                            onCompleteDraw(null);
                            updateAreaTooltip(null);
                        }

                    } else {
                        onCompleteDraw(null);
                        updateAreaTooltip(null); // Ensure tooltip is hidden when no features are present
                    }
                }
            }
            if (type === "POINT") {
                if (draw.current) {
                    // TODO Implement the Point draw and onCompleteDraw return the POINT() as centroid_wkt prop
                    const data = draw.current.getAll();
                    const points = MapboxFeature.parseStringFromFeature(data);
                    if (points) {
                        onCompleteDraw({
                            centroid_wkt: points,
                            boundary: null,
                            area: null,
                        } as unknown as ParsedFeatureData);
                    } else {
                        onCompleteDraw(null);
                        updateAreaTooltip(null);
                    }
                }
            }
        };

        // Attach event listeners for draw events.
        map.current.on("draw.create", updateFeatures);
        map.current.on("draw.delete", updateFeatures);
        map.current.on("draw.update", updateFeatures);

        // Cleanup function to remove the map and marker on component unmount.
        return () => {
            if (searchMarkerRef.current) {
                searchMarkerRef.current.remove();
            }
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array ensures this effect runs only once on mount.

    // Effect to fly to the new map center or zoom when they change.
    useEffect(() => {
        if (map.current && isMapLoaded) {
            // Animate map transition to the new center and zoom.
            map.current.flyTo({ center: mapCenter as LngLatLike, zoom: mapZoom, duration: 1000 });
        }
    }, [mapCenter, mapZoom, isMapLoaded]);

    // Effect to manage the search result marker on the map.
    useEffect(() => {
        if (!map.current || !isMapLoaded) return;

        // Remove the previous search marker if it exists.
        if (searchMarkerRef.current) {
            searchMarkerRef.current.remove();
            searchMarkerRef.current = null;
        }

        if (searchMarkerCoordinates && type === 'POLYGON') {
            const newMarker = new mapboxgl.Marker()
                .setLngLat(searchMarkerCoordinates as LngLatLike) // `LngLatLike` is expected by `setLngLat`
                .addTo(map.current);
            searchMarkerRef.current = newMarker;
        }
        // This effect depends on searchMarkerCoordinates and isMapLoaded.
        // It ensures the marker is updated correctly when a new search result is available and the map is ready.
    }, [searchMarkerCoordinates, isMapLoaded]);

    const handleRetrieve = debounce((res: SearchBoxRetrieveResponse) => {
        if (res.features && res.features.length > 0) {
            const feature = res.features[0];
            const coordinates = feature.geometry.coordinates as [number, number];
            // Prefer full_address, then place_formatted, then name, with a generic fallback.
            // This provides the most comprehensive address available.
            const address = feature.properties.full_address ||
                feature.properties.place_formatted ||
                feature.properties.name ||
                "Selected Location";

            if (coordinates) {
                handleAddressSelect(coordinates, address);
            } else {
                handleAddressSelect(null, address);
                showSnackbar({
                    message: `Coordinates not available for ${address}.`,
                    type: snackbarType.ERROR,
                });
            }
        } else {
            // If no features are found from the search.
            handleAddressSelect(null);
            showSnackbar({
                message: 'Address Not Found. Could not retrieve details for the selected address.',
                type: snackbarType.ERROR,
            });
        }
    }, 400); // Debounce to prevent excessive API calls during typing.

    /**
     * Handles the selection of an address from the search results.
     * Updates the map's center, zoom, and places a marker.
     * @param coordinates The geographic coordinates [lng, lat] of the selected address, or null.
     * @param address The formatted address string, or undefined.
     */
    const handleAddressSelect = (coordinates: [number, number] | null, address?: string) => {
        setInputValue(address || ''); // Update the SearchBox input field.

        if (coordinates) {
            setMapCenter(coordinates);
            setMapZoom(15); // Zoom in to a more detailed view of the selected address.
            showSnackbar({
                message: address ? `Map centered on ${address}` : 'Map centered on selected location.',
                type: snackbarType.INFO
            });
            setSearchMarkerCoordinates(coordinates);
        }
        // If coordinates are null (e.g., address not found or no coordinates available),
        // the marker won't be set/updated, and the map won't recenter based on new coordinates.
        // The input value is cleared if `address` is also undefined.
    };

    /**
     * Updates the state controlling the area tooltip based on the parsed feature data.
     * @param data The parsed feature data containing boundary, centroid, area, etc.
     */
    const updateAreaTooltip = (data: ParsedFeatureData | null) => {
        if (data && data.area !== null && data.area !== undefined && data.area > 0) {
            const formattedArea = roundToTwoDecimalPlaces(data.area);
            setAreaTooltipContent(`Area: ${formattedArea} ha`);
            setShowAreaTooltip(true);
        } else {
            setAreaTooltipContent('');
            setShowAreaTooltip(false);
        }
    };

    return (
        <>
            <div style={{
                position: 'relative', // Establishes a positioning context for absolute children
                width: '100%',      // Takes full width of its container in DialogContent
                flex: 1,            // Takes available vertical space if DialogContent is a flex column
                minHeight: '400px'  // Ensures a minimum height for the map to be usable.
            }}>
                <div
                    ref={mapContainer}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                    }}
                    aria-label="Interactive map for drawing polygons and viewing locations"
                />
                <div style={{
                    position: 'absolute',
                    top: '20px', // Adjusted for better spacing from top, considering Dialog padding
                    left: '50%', // Center the search box horizontally.
                    transform: 'translateX(-50%)', // Precise horizontal centering.
                    zIndex: 1, // Ensure SearchBox is rendered above the map.
                    width: 'calc(100% - 40px)', // Responsive width, considering some padding from edges.
                    maxWidth: '500px' // Max width for the search box
                }}>
                    <SearchBox
                        accessToken={mapBoxConfig.accessToken || ''}
                        value={inputValue}
                        onChange={(text: string) => setInputValue(text)}
                        onRetrieve={handleRetrieve}
                        options={{
                            language: "en",
                            types: "address,postcode,place,locality,neighborhood",
                            limit: 5,
                        }}
                    />
                </div>
            </div>
            {/* Area Tooltip */}
            {showAreaTooltip && (
                <div style={{
                    position: 'absolute',
                    top: '110px', // Position below the NavigationControl (approx)
                    right: '10px',
                    zIndex: 1, // Ensure it's above the map
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    fontSize: '1.5em',
                }}>
                    {areaTooltipContent}
                </div>
            )}
        </>
    );
};

export default MapBoxDrawer;